import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { debounceTime, firstValueFrom, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UAParser } from 'ua-parser-js';
import { IGeolocationInfo } from '../interfaces';
import { DeviceModelRequestDto } from '../models';
import { UserAccountService } from './user-account.service';
import { UserDeviceService } from './user-device.service';

@Injectable({ providedIn: 'root' })
export class FingerprintService {
  readonly #httpClient = inject(HttpClient);
  readonly #userDeviceService = inject(UserDeviceService);
  readonly #userAccountService = inject(UserAccountService);

  #parser!: UAParser;

  initializeFingerprint() {
    const { userAgent } = window.navigator;
    this.#parser = new UAParser(userAgent);
    this.generateDeviceFingerprint().then(device => {
      if (device) {
        this.#userDeviceService.loginDevice(device).then(() => {
          // Notify UserAccountService about the new JWT from device auth
          const deviceJWT = this.#userDeviceService.deviceJWT();
          if (deviceJWT) {
            this.#userAccountService.setUserAccount(deviceJWT);
          }
        });
      }
    });
  }

  private async generateDeviceFingerprint(): Promise<DeviceModelRequestDto | undefined> {
    const { language, geolocation, hardwareConcurrency, platform } = window.navigator;
    const deviceDetails = this.#parser.getResult();

    // Get geolocation with a timeout to prevent hanging during SSR
    let geolocationData;
    try {
      geolocationData = await Promise.race([
        this.getGeolocationInfo(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Geolocation timeout')), 3000)),
      ]);
    } catch {
      // If geolocation fails, use default values
      geolocationData = {
        country: '',
        country_code: '',
        city: '',
        latitude: 0,
        longitude: 0,
        ip: '',
        security: null,
      };
    }

    const { country, country_code, city, latitude, longitude, ip, security } = geolocationData as IGeolocationInfo;
    const hashObject = {
      browser_name: deviceDetails.browser.name,
      browser_version: deviceDetails.browser.version,
      browser_major: deviceDetails.browser.major,
      device_type: deviceDetails.device.type,
      device_model: deviceDetails.device.model,
      os_name: deviceDetails.os.name,
      os_version: deviceDetails.os.version,
      country,
      country_code,
      city,
      latitude,
      longitude,
      ip,
      language,
      geolocation,
      hardwareConcurrency,
      platform,
      color_depth: window.screen.colorDepth,
    };

    if (security && (security.proxy || security.hosting || security.vpn)) {
      return;
    }

    const hashedValue = await this.#hashObject(hashObject);

    const device: DeviceModelRequestDto = {
      name: hashObject.device_model || `${hashObject.os_name || ''} ${hashObject.os_version || ''}`,
      visitorId: hashedValue,
      userAgent: `${hashObject.os_name} ${hashObject.os_version} - ${hashObject.browser_name} ${hashObject.browser_version}`,
    };

    return device;
  }

  async #hashObject(obj: Record<string, unknown>): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(obj));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  getGeolocationInfo() {
    return firstValueFrom(
      this.#httpClient
        .get<IGeolocationInfo>(environment.GEOLOCATION_API_URL, {
          headers: {
            accept: 'application/json',
          },
        })
        .pipe(debounceTime(500), shareReplay(1)),
    );
  }
}
